// useWeather — fetch del clima vía Open-Meteo (gratis, sin API key).
// Comportamiento adaptativo:
//   - 0–16 días desde hoy: forecast real (precisión decreciente)
//   - >16 días: promedio histórico de los últimos 5 años para esa fecha
// Cache: localStorage por (lat, lng, date) durante 6h para evitar re-fetch.
import { useEffect, useState } from "react";

const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 horas

function cacheKey(lat, lng, date) {
  return `weather:${lat.toFixed(2)}:${lng.toFixed(2)}:${date}`;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    // Defensive: if a previous version of this code cached a broken response
    // (tmax/tmin === 0 because of Math.round(null)), discard it.
    if (data && data.tmax === 0 && data.tmin === 0) return null;
    return data;
  } catch { return null; }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* quota or private mode — ignore */ }
}

// Weather code → human-friendly icon + label (WMO codes)
const WEATHER_INFO = {
  0:  { icon: "☀️",  label: "Despejado" },
  1:  { icon: "🌤️",  label: "Mayormente despejado" },
  2:  { icon: "⛅",  label: "Parcialmente nublado" },
  3:  { icon: "☁️",  label: "Nublado" },
  45: { icon: "🌫️",  label: "Niebla" },
  48: { icon: "🌫️",  label: "Niebla con escarcha" },
  51: { icon: "🌦️",  label: "Llovizna ligera" },
  53: { icon: "🌦️",  label: "Llovizna" },
  55: { icon: "🌧️",  label: "Llovizna intensa" },
  61: { icon: "🌧️",  label: "Lluvia ligera" },
  63: { icon: "🌧️",  label: "Lluvia" },
  65: { icon: "⛈️",  label: "Lluvia intensa" },
  71: { icon: "🌨️",  label: "Nieve ligera" },
  73: { icon: "🌨️",  label: "Nieve" },
  75: { icon: "❄️",  label: "Nieve intensa" },
  80: { icon: "🌦️",  label: "Chubascos" },
  81: { icon: "🌧️",  label: "Chubascos fuertes" },
  82: { icon: "⛈️",  label: "Chubascos violentos" },
  95: { icon: "⛈️",  label: "Tormenta" },
  96: { icon: "⛈️",  label: "Tormenta con granizo" },
  99: { icon: "⛈️",  label: "Tormenta fuerte con granizo" },
};

export function weatherInfo(code) {
  return WEATHER_INFO[code] || { icon: "🌡️", label: "—" };
}

/** Days between two YYYY-MM-DD dates (signed) */
function daysBetween(isoFrom, isoTo) {
  const a = new Date(isoFrom + "T12:00:00Z");
  const b = new Date(isoTo + "T12:00:00Z");
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

async function fetchForecast(lat, lng, date) {
  // Open-Meteo forecast API supports up to 16 days. We ask for that specific date.
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,sunrise,sunset,wind_speed_10m_max&timezone=auto&start_date=${date}&end_date=${date}`;
  const r = await fetch(url);
  if (!r.ok) throw new Error("Forecast failed");
  const j = await r.json();
  const i = 0;
  if (!j?.daily?.weather_code?.length) return null;
  // Edge of forecast horizon: API sometimes returns null for tmax/tmin even
  // though it returned a row. Signal "no usable forecast" so caller can fall
  // back to climatology.
  const tmaxRaw = j.daily.temperature_2m_max?.[i];
  const tminRaw = j.daily.temperature_2m_min?.[i];
  if (tmaxRaw == null || tminRaw == null) return null;
  return {
    kind: "forecast",
    code: j.daily.weather_code[i],
    tmax: Math.round(tmaxRaw),
    tmin: Math.round(tminRaw),
    rain: j.daily.precipitation_sum?.[i] ?? 0,
    rainProb: j.daily.precipitation_probability_max?.[i] ?? 0,
    sunrise: j.daily.sunrise?.[i],
    sunset: j.daily.sunset?.[i],
    wind: Math.round(j.daily.wind_speed_10m_max?.[i] ?? 0),
  };
}

async function fetchClimatology(lat, lng, date) {
  // Promedio histórico de los últimos 5 años para esa fecha (mismo día/mes).
  // Open-Meteo Archive API — datos verificados.
  const month = date.slice(5, 7);
  const day = date.slice(8, 10);
  const currentYear = new Date().getFullYear();
  const promises = [];
  for (let yr = currentYear - 5; yr < currentYear; yr++) {
    const dt = `${yr}-${month}-${day}`;
    const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=${dt}&end_date=${dt}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weather_code&timezone=auto`;
    promises.push(fetch(url).then((r) => r.ok ? r.json() : null).catch(() => null));
  }
  const responses = (await Promise.all(promises)).filter((r) => r?.daily?.temperature_2m_max?.[0] != null);
  if (responses.length === 0) return null;
  const avg = (vals) => vals.reduce((a, b) => a + b, 0) / vals.length;
  const tmaxs = responses.map((r) => r.daily.temperature_2m_max[0]);
  const tmins = responses.map((r) => r.daily.temperature_2m_min[0]);
  const rains = responses.map((r) => r.daily.precipitation_sum[0] || 0);
  const codes = responses.map((r) => r.daily.weather_code[0]);
  // Pick the most common weather code from the historical samples
  const codeCount = {};
  codes.forEach((c) => { codeCount[c] = (codeCount[c] || 0) + 1; });
  const dominantCode = parseInt(Object.entries(codeCount).sort((a, b) => b[1] - a[1])[0][0], 10);
  return {
    kind: "historical",
    code: dominantCode,
    tmax: Math.round(avg(tmaxs)),
    tmin: Math.round(avg(tmins)),
    rain: +avg(rains).toFixed(1),
    sampleYears: responses.length,
  };
}

/**
 * useWeather({ lat, lng, date })
 *   - lat, lng: location coords
 *   - date: YYYY-MM-DD
 * Returns: { loading, data, error, daysAway }
 *   data shape:
 *     { kind: 'forecast' | 'historical', code, tmax, tmin, rain, rainProb?, ... }
 */
export function useWeather({ lat, lng, date }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const daysAway = date ? daysBetween(todayIso(), date) : 0;

  useEffect(() => {
    if (!lat || !lng || !date) {
      setState({ loading: false, data: null, error: "missing-params" });
      return;
    }
    let cancelled = false;
    const key = cacheKey(lat, lng, date);
    const cached = readCache(key);
    if (cached) {
      setState({ loading: false, data: cached, error: null });
      return;
    }
    setState({ loading: true, data: null, error: null });
    const useForecast = daysAway >= 0 && daysAway <= 16;
    const fetcher = useForecast
      ? fetchForecast(lat, lng, date).then((d) => d || fetchClimatology(lat, lng, date))
      : fetchClimatology(lat, lng, date);

    fetcher.then((data) => {
      if (cancelled) return;
      if (data) {
        writeCache(key, data);
        setState({ loading: false, data, error: null });
      } else {
        setState({ loading: false, data: null, error: "no-data" });
      }
    }).catch((err) => {
      if (!cancelled) setState({ loading: false, data: null, error: err.message || "fetch-error" });
    });

    return () => { cancelled = true; };
  }, [lat, lng, date, daysAway]);

  return { ...state, daysAway };
}
